import {
  IAutomationTiming,
  IEvent,
  isRelativeTimingWithTime,
  TimingDirection,
  TimingUnit,
} from '@principle-theorem/principle-core/interfaces';
import {
  DayOfWeek,
  DAYS_OF_WEEK,
  formatTimeStringDisplay,
  ITimestampRange,
  mergeDayAndTime,
  Timestamp,
  Timezone,
  toDayOfWeek,
  toMomentTz,
  toTimestamp,
} from '@principle-theorem/shared';
import { isEqual } from 'lodash';
import * as moment from 'moment-timezone';
import { TimezoneResolver } from '../../timezone';
import { RecurrenceDirection } from '../schedule/recurrence-pattern';

export class AutomationTiming {
  static async resolveTriggerDateFromEvent(
    event: IEvent,
    timing: IAutomationTiming
  ): Promise<Timestamp> {
    const timezone = await TimezoneResolver.fromPracticeRef(event.practice.ref);
    return AutomationTiming.getTriggerDateFromEvent(event, timing, timezone);
  }

  static getTriggerDateFromEvent(
    event: ITimestampRange,
    timing: IAutomationTiming,
    timezone: Timezone
  ): Timestamp {
    if (timing.direction === TimingDirection.Immediately) {
      return toTimestamp(moment.tz(timezone));
    }

    const baseTime =
      timing.direction === TimingDirection.Before
        ? toMomentTz(event.from, timezone)
        : toMomentTz(event.to, timezone);

    const eventTime = isRelativeTimingWithTime(timing)
      ? mergeDayAndTime(baseTime, timing.time, timezone)
      : baseTime;

    const recurrenceDirection =
      timing.direction === TimingDirection.Before
        ? RecurrenceDirection.Backward
        : RecurrenceDirection.Forward;

    return toTimestamp(
      AutomationTiming.getNextValidOccurrence(
        eventTime,
        timing.daysOfWeek,
        recurrenceDirection,
        timezone,
        timing.countOnlySelectedDays ?? false,
        timing.unit,
        timing.amount
      )
    );
  }

  static getTimingSummary(timing: IAutomationTiming): string {
    if (timing.direction === TimingDirection.Immediately) {
      return 'Immediately';
    }

    const timingUnit = this._getUnit(timing);
    return `${timing.amount} ${timingUnit} ${timing.direction}${
      timing.time ? ' at ' + formatTimeStringDisplay(timing.time) : ''
    }`;
  }

  static getNextValidOccurrence(
    initialDate: moment.Moment,
    daysOfWeek: DayOfWeek[] = [],
    direction: RecurrenceDirection,
    timezone: Timezone,
    countOnlySelectedDays: boolean,
    unit: TimingUnit,
    amount?: number
  ): moment.Moment {
    const hasCustomDaysOfWeek =
      daysOfWeek.length > 0 && !isEqual(daysOfWeek, DAYS_OF_WEEK);

    if (!hasCustomDaysOfWeek) {
      return adjustBasicOccurrence(initialDate, direction, unit, amount);
    }

    if (countOnlySelectedDays && !!amount && unit === TimingUnit.Days) {
      return getOccurrenceByValidDays(
        initialDate,
        daysOfWeek,
        timezone,
        direction,
        amount
      );
    }

    return adjustToNextValidDay(
      initialDate,
      daysOfWeek,
      timezone,
      direction,
      unit,
      amount
    );
  }

  private static _getUnit(timing: IAutomationTiming): string {
    const isPlural: boolean =
      timing.unit.lastIndexOf('s') === timing.unit.length - 1;
    if (isPlural) {
      return timing.amount === 1
        ? timing.unit.substring(0, timing.unit.lastIndexOf('s'))
        : timing.unit;
    }
    return timing.amount === 1 ? timing.unit : `${timing.unit}s`;
  }
}

function adjustBasicOccurrence(
  occurrence: moment.Moment,
  direction: RecurrenceDirection,
  unit: TimingUnit,
  amount?: number
): moment.Moment {
  return direction === RecurrenceDirection.Backward
    ? occurrence.subtract(amount, unit)
    : occurrence.add(amount, unit);
}

function getOccurrenceByValidDays(
  initialDate: moment.Moment,
  daysOfWeek: DayOfWeek[],
  timezone: Timezone,
  direction: RecurrenceDirection,
  amount: number
): moment.Moment {
  const result = initialDate.clone();
  let validDaysCounted = 0;

  while (
    !isValidDayOfWeek(result, daysOfWeek, timezone) ||
    validDaysCounted < amount
  ) {
    if (direction === RecurrenceDirection.Backward) {
      result.subtract(1, 'day');
    } else {
      result.add(1, 'day');
    }

    if (isValidDayOfWeek(result, daysOfWeek, timezone)) {
      validDaysCounted++;
    }
  }

  return result;
}

function adjustToNextValidDay(
  initialDate: moment.Moment,
  daysOfWeek: DayOfWeek[],
  timezone: Timezone,
  direction: RecurrenceDirection,
  unit: TimingUnit,
  amount?: number
): moment.Moment {
  const adjustedDate = adjustBasicOccurrence(
    initialDate.clone(),
    direction,
    unit,
    amount
  );

  while (!isValidDayOfWeek(adjustedDate, daysOfWeek, timezone)) {
    if (direction === RecurrenceDirection.Backward) {
      adjustedDate.subtract(1, 'day');
    } else {
      adjustedDate.add(1, 'day');
    }
  }
  return adjustedDate;
}

function isValidDayOfWeek(
  date: moment.Moment,
  daysOfWeek: DayOfWeek[],
  timezone: Timezone
): boolean {
  return daysOfWeek.includes(toDayOfWeek(date, timezone));
}
